(() => {
  const normalize = value => String(value).toLocaleLowerCase('de').replace(/ß/g, 'ss').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/(\d)[\s-]+(?=\d)/g, '$1').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const init = root => {
    if (root.dataset.browseInitialized) return;
    root.dataset.browseInitialized = 'true';
    const keywordEnabled = root.dataset.keywordSearch === 'true';
    const collectionURL = new URL(root.dataset.collectionUrl, location.origin);
    const filterForm = root.querySelector('.lmp-filter-form');
    const sortForm = root.querySelector('.lmp-sort-form');
    const currentQuery = () => keywordEnabled ? (new URL(location.href).searchParams.get('lmp_q') || '').trim().slice(0, 120) : '';
    const withState = () => {
      const url = new URL(location.href);
      url.pathname = collectionURL.pathname;
      url.hash = '';
      return url;
    };
    const updateLinks = () => {
      const current = new URL(location.href);
      root.querySelectorAll('[data-lmp-query-state]').forEach(input => { input.value = currentQuery(); });
      root.querySelectorAll('.lmp-quick a, .lmp-active a, .lmp-filters__actions a, .lmp-no-results a').forEach(link => {
        if (!link.dataset.lmpOriginalHref) link.dataset.lmpOriginalHref = link.getAttribute('href');
        const url = new URL(link.dataset.lmpOriginalHref, location.origin);
        if (url.origin !== location.origin || url.pathname !== collectionURL.pathname || url.searchParams.has('flyweight-chat')) return;
        ['page', 'lmp_page', 'lmp_q'].forEach(key => url.searchParams.delete(key));
        if (currentQuery()) url.searchParams.set('lmp_q', currentQuery());
        ['sort_by', 'preview_theme_id'].forEach(key => {
          if (current.searchParams.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
        });
        link.href = url.toString();
      });
    };
    [filterForm, sortForm].filter(Boolean).forEach(form => {
      form.addEventListener('submit', event => {
        event.preventDefault();
        const url = withState();
        [...url.searchParams.keys()].forEach(key => {
          if (key.startsWith('filter.') || ['sort_by', 'page', 'lmp_page', 'lmp_q', 'flyweight-chat'].includes(key)) url.searchParams.delete(key);
        });
        new FormData(form).forEach((value, key) => {
          if (String(value).trim() && key !== 'lmp_q') url.searchParams.append(key, value);
        });
        if (currentQuery()) url.searchParams.set('lmp_q', currentQuery());
        location.assign(url.toString());
      });
    });
    sortForm?.querySelector('[data-lmp-sort]')?.addEventListener('change', () => sortForm.requestSubmit());
    updateLinks();
    if (!keywordEnabled) return;

    const panel = root.querySelector('[data-lmp-keyword]');
    const searchForm = panel.querySelector('form');
    const input = searchForm.querySelector('[name="lmp_q"]');
    const status = panel.querySelector('[data-lmp-search-status]');
    const clearButton = panel.querySelector('[data-lmp-clear-search]');
    const nativeResults = root.querySelector('[data-lmp-native-results]');
    const searchResults = root.querySelector('[data-lmp-search-results]');
    const count = root.querySelector('.lmp-result-count');
    const countOriginal = count.innerHTML;
    const texts = JSON.parse(root.querySelector('[data-lmp-search-texts]').textContent);
    const applyButton = filterForm?.querySelector('button[type="submit"]');
    const applyOriginal = applyButton?.textContent;
    const pageSize = Math.min(48, Math.max(1, Number(root.dataset.searchPageSize) || 24));
    let recordsPromise;
    let generation = 0;
    panel.hidden = false;

    const feedURL = page => {
      const url = new URL(collectionURL);
      new URL(location.href).searchParams.forEach((value, key) => {
        if (key.startsWith('filter.') || ['sort_by', 'preview_theme_id'].includes(key)) url.searchParams.append(key, value);
      });
      url.searchParams.set('view', root.dataset.searchFeedView || 'lmp-search-feed');
      url.searchParams.set('page', String(page));
      return url;
    };
    const loadPage = async page => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 25000);
      try {
        const response = await fetch(feedURL(page), { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = doc.querySelector('[data-lmp-feed]');
        if (!feed || feed.dataset.collectionId !== root.dataset.collectionId || Number(feed.dataset.page) !== page) throw new Error('Invalid collection response');
        const total = Number(feed.dataset.count);
        const pages = Math.max(1, Number(feed.dataset.pages));
        if (!Number.isInteger(total) || total < 0 || total > 25000 || !Number.isInteger(pages) || pages > 250) throw new Error('Collection exceeds search limit');
        const records = [...feed.querySelectorAll('template[data-lmp-record]')].map(template => {
          const content = template.content;
          const description = document.createElement('template');
          description.innerHTML = content.querySelector('[data-lmp-record-description]').textContent;
          description.content.querySelectorAll('script, style, noscript, template').forEach(node => node.remove());
          const title = content.querySelector('[data-lmp-record-title]').textContent;
          const meta = content.querySelector('[data-lmp-record-meta]').textContent;
          const card = content.querySelector('.lmp-card');
          if (!card || !template.dataset.productId) throw new Error('Invalid product record');
          return { id: template.dataset.productId, text: normalize([title, meta, description.content.textContent].join(' ')), card };
        });
        return { total, pages, records };
      } finally {
        window.clearTimeout(timeout);
      }
    };
    const loadRecords = () => {
      if (!recordsPromise) {
        recordsPromise = (async () => {
          const first = await loadPage(1);
          const results = [first];
          let nextPage = 2;
          const worker = async () => {
            while (nextPage <= first.pages) {
              const page = nextPage++;
              const result = await loadPage(page);
              if (result.total !== first.total || result.pages !== first.pages) throw new Error('Collection changed during search');
              results[page - 1] = result;
            }
          };
          await Promise.all(Array.from({ length: Math.min(3, first.pages - 1) }, worker));
          const records = results.flatMap(result => result.records);
          if (records.length !== first.total || new Set(records.map(record => record.id)).size !== first.total) throw new Error('Incomplete collection search');
          return records;
        })().catch(error => { recordsPromise = null; throw error; });
      }
      return recordsPromise;
    };
    const clearSearch = () => {
      const url = withState();
      ['lmp_q', 'lmp_page', 'page', 'flyweight-chat'].forEach(key => url.searchParams.delete(key));
      location.assign(url.toString());
    };
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const button = (label, action) => {
      const node = element('button', 'lmp-search-action', label);
      node.type = 'button';
      node.addEventListener('click', action);
      return node;
    };
    const pageURL = page => {
      const url = withState();
      url.searchParams.delete('page');
      if (page === 1) url.searchParams.delete('lmp_page');
      else url.searchParams.set('lmp_page', String(page));
      return url;
    };
    const render = async (focusResults = false) => {
      const version = ++generation;
      const query = currentQuery();
      input.value = query;
      clearButton.hidden = !query;
      root.classList.toggle('has-keyword-query', Boolean(query));
      updateLinks();
      nativeResults.hidden = Boolean(query);
      searchResults.hidden = !query;
      if (applyButton) applyButton.textContent = query ? texts.filterApply : applyOriginal;
      if (!query) {
        status.textContent = '';
        count.innerHTML = countOriginal;
        return;
      }
      status.textContent = texts.loading;
      count.textContent = '…';
      searchResults.setAttribute('aria-busy', 'true');
      searchResults.replaceChildren();
      try {
        const records = await loadRecords();
        if (version !== generation) return;
        const tokens = normalize(query).split(' ').filter(Boolean);
        const matches = tokens.length ? records.filter(record => tokens.every(token => record.text.includes(token))) : [];
        const pages = Math.max(1, Math.ceil(matches.length / pageSize));
        const requestedPage = Number(new URL(location.href).searchParams.get('lmp_page')) || 1;
        const page = Math.min(pages, Math.max(1, Math.floor(requestedPage)));
        if (page !== requestedPage) history.replaceState(null, '', pageURL(page));
        status.textContent = texts.results.replace('__COUNT__', String(matches.length)).replace('__QUERY__', query);
        count.replaceChildren(element('strong', '', String(matches.length)), document.createTextNode(' ' + texts.hits));
        if (!matches.length) {
          const empty = element('div', 'lmp-no-results');
          empty.append(element('h2', '', texts.noResults), element('p', '', texts.noResultsHelp), button(texts.clearSearch, clearSearch));
          searchResults.append(empty);
        } else {
          const grid = element('div', 'lmp-grid');
          matches.slice((page - 1) * pageSize, page * pageSize).forEach(record => grid.append(record.card.cloneNode(true)));
          searchResults.append(grid);
          if (pages > 1) {
            const nav = element('nav', 'lmp-pagination');
            nav.setAttribute('aria-label', texts.paginationLabel);
            const addLink = (number, label) => {
              const link = element('a', '', label);
              link.href = pageURL(number).toString();
              link.dataset.lmpSearchPage = String(number);
              link.setAttribute('aria-label', texts.pageLabel.replace('__CURRENT__', String(number)).replace('__TOTAL__', String(pages)));
              nav.append(link);
            };
            if (page > 1) addLink(page - 1, '‹');
            const numbers = [...new Set([1, page - 1, page, page + 1, pages])].filter(number => number >= 1 && number <= pages).sort((a, b) => a - b);
            numbers.forEach((number, index) => {
              if (index && number - numbers[index - 1] > 1) nav.append(element('span', 'lmp-pagination__gap', '…'));
              if (number === page) {
                const current = element('span', 'is-current', String(number));
                current.setAttribute('aria-current', 'page');
                current.setAttribute('aria-label', texts.pageLabel.replace('__CURRENT__', String(number)).replace('__TOTAL__', String(pages)));
                nav.append(current);
              } else addLink(number, String(number));
            });
            if (page < pages) addLink(page + 1, '›');
            searchResults.append(nav);
          }
        }
        searchResults.setAttribute('aria-busy', 'false');
        if (focusResults) {
          searchResults.focus({ preventScroll: true });
          root.querySelector('.lmp-toolbar').scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      } catch (error) {
        if (version !== generation) return;
        status.textContent = texts.error;
        count.textContent = '—';
        const empty = element('div', 'lmp-no-results');
        empty.append(element('p', '', texts.error), button(texts.retry, () => render()));
        searchResults.replaceChildren(empty);
        searchResults.setAttribute('aria-busy', 'false');
      }
    };
    searchForm.addEventListener('submit', event => {
      event.preventDefault();
      const query = input.value.trim().slice(0, 120);
      if (!query) { clearSearch(); return; }
      const url = withState();
      ['page', 'lmp_page', 'flyweight-chat'].forEach(key => url.searchParams.delete(key));
      url.searchParams.set('lmp_q', query);
      history.pushState(null, '', url);
      render();
    });
    clearButton.addEventListener('click', clearSearch);
    searchResults.addEventListener('click', event => {
      const link = event.target.closest('a[data-lmp-search-page]');
      if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      history.pushState(null, '', link.href);
      render(true);
    });
    window.addEventListener('popstate', () => {
      if (!root.isConnected) return;
      if (currentQuery()) render();
      else location.reload();
    });
    render();
  };
  const initSpick = root => {
    if (root.dataset.searchInitialized) return;
    const page = root.closest('.lmp-spick');
    const grid = page?.querySelector('.lmp-spick__products');
    const panel = root.querySelector('[data-lmp-keyword]');
    if (!grid || !panel) return;
    root.dataset.searchInitialized = 'true';
    const form = panel.querySelector('form');
    const input = form.querySelector('[name="lmp_q"]');
    const status = panel.querySelector('[data-lmp-search-status]');
    const clear = panel.querySelector('[data-lmp-clear-search]');
    const texts = JSON.parse(root.querySelector('[data-lmp-search-texts]').textContent);
    const records = [...grid.querySelectorAll('.lmp-spick__product')].map(card => ({ card, text: normalize(card.textContent) }));
    const empty = document.createElement('p');
    empty.className = 'lmp-spick-search-empty';
    empty.textContent = texts.noResults + '. ' + texts.noResultsHelp;
    empty.hidden = true;
    grid.before(empty);
    panel.hidden = false;
    const render = () => {
      const query = (new URL(location.href).searchParams.get('lmp_q') || '').trim().slice(0, 120);
      input.value = query;
      const tokens = normalize(query).split(' ').filter(Boolean);
      let count = 0;
      records.forEach(record => {
        const match = !query || (tokens.length > 0 && tokens.every(token => record.text.includes(token)));
        record.card.hidden = !match;
        if (match) count++;
      });
      status.textContent = query ? texts.results.replace('__COUNT__', String(count)).replace('__QUERY__', query) : '';
      clear.hidden = !query;
      empty.hidden = !query || count > 0;
    };
    const search = query => {
      const url = new URL(location.href);
      ['lmp_q', 'page', 'lmp_page', 'flyweight-chat'].forEach(key => url.searchParams.delete(key));
      if (query) url.searchParams.set('lmp_q', query);
      history.pushState(null, '', url);
      render();
    };
    form.addEventListener('submit', event => { event.preventDefault(); search(input.value.trim().slice(0, 120)); });
    clear.addEventListener('click', () => search(''));
    window.addEventListener('popstate', () => { if (root.isConnected) render(); });
    render();
  };
  const initAll = scope => {
    if (scope.matches?.('[data-lmp-browse]')) init(scope);
    scope.querySelectorAll?.('[data-lmp-browse]').forEach(init);
    if (scope.matches?.('[data-lmp-spick-search]')) initSpick(scope);
    scope.querySelectorAll?.('[data-lmp-spick-search]').forEach(initSpick);
  };
  initAll(document);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();

